import { useCallback, useState } from "react";

import { Badge, Button, Icons } from "../../../shared/ui";
import { AdminListRow } from "../AdminListRow";
import type { OperatorSummary } from "./accessApi";
import type { AccessEnv } from "./accessEnv";
import { operatorName, roleLabel } from "./accessFormat";
import { AddOperatorSheet } from "./AddOperatorSheet";
import { LoadGate } from "./LoadGate";
import { useAccessLoad } from "./useAccessLoad";
import { useAccessT } from "./useAccessT";

/** Operators master list: name, role badges, state, chevron. */
export function OperatorList({
  env,
  token,
  selectedId,
  hrefFor,
  onOpen,
}: {
  env: AccessEnv;
  token: number;
  selectedId: string | null;
  hrefFor: (operatorId: string) => string;
  onOpen: (operatorId: string) => void;
}) {
  const t = useAccessT();
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => env.api.listOperators(), [env.api]);
  const { state, retry } = useAccessLoad(load, token);
  return (
    <div className="flex flex-col gap-3">
      {env.canManage && (
        <Button
          variant="secondary"
          onClick={() => {
            setAdding(true);
          }}
        >
          {t("admin.access.operator.add")}
        </Button>
      )}
      <LoadGate state={state} retry={retry}>
        {(operators) =>
          operators.length === 0 ? (
            <p className="text-body text-fg-muted">{t("admin.access.empty")}</p>
          ) : (
            <ul
              aria-label={t("admin.access.list.operators")}
              className="divide-y divide-line-subtle overflow-hidden rounded-xl bg-raised shadow-card"
            >
              {operators.map((operator) => (
                <li key={operator.operator_id}>
                  <OperatorRow
                    operator={operator}
                    selected={operator.operator_id === selectedId}
                    href={hrefFor(operator.operator_id)}
                    onOpen={onOpen}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </LoadGate>
      {adding && (
        <AddOperatorSheet
          env={env}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function OperatorRow({
  operator,
  selected,
  href,
  onOpen,
}: {
  operator: OperatorSummary;
  selected: boolean;
  href: string;
  onOpen: (operatorId: string) => void;
}) {
  const t = useAccessT();
  return (
    <AdminListRow
      href={href}
      selected={selected}
      onSelect={() => {
        onOpen(operator.operator_id);
      }}
    >
      <span className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-callout font-semibold text-fg">
          {operatorName(operator)}
          {operator.is_self && (
            <span className="ml-1 text-caption font-normal text-fg-muted">
              {`(${t("admin.access.operator.self")})`}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <Badge tone={operator.active ? "success" : "neutral"}>
            {operator.active
              ? t("admin.access.operator.active")
              : t("admin.access.operator.inactive")}
          </Badge>
          <Icons.ChevronRight size={16} className="text-fg-subtle" />
        </span>
      </span>
      <span className="flex flex-wrap gap-1">
        {operator.roles.map((role) => (
          <Badge key={role.role_id} tone="accent">
            {roleLabel(t, role.role_id, role.display_name)}
          </Badge>
        ))}
      </span>
    </AdminListRow>
  );
}
